/**
 * Sentry error tracking setup.
 */

import * as Sentry from '@sentry/vue'
import type { App } from 'vue'
import { storageService } from '../services/storageService'
import type { PermissionsData } from '../services/permissionsService'
import type { eventBus as EventBusInstance } from '../eventBus'
import { log } from '../utils/DebugLogger'

interface SentryFilterData {
  Franquias?: Record<string, unknown>
  DataInicio?: string
  DataFim?: string
}

interface SentryFilterContext {
  franchise_count: number
  franchise_ids: string
  date_range: string
  date_start: string
  date_end: string
  timestamp?: string
  last_updated?: string
}

const parseSentryFilterContext = (filterData: SentryFilterData): SentryFilterContext => {
  const franchiseIds = Object.keys(filterData.Franquias || {})
  const dateRange =
    filterData.DataInicio && filterData.DataFim ? `${filterData.DataInicio} - ${filterData.DataFim}` : 'not set'

  return {
    franchise_count: franchiseIds.length,
    franchise_ids: franchiseIds.join(', '),
    date_range: dateRange,
    date_start: filterData.DataInicio || 'not set',
    date_end: filterData.DataFim || 'not set'
  }
}

interface StoredFilterEntry {
  data?: SentryFilterData
  timestamp?: number
}

const loadInitialFilterContext = (): void => {
  const parsedData = storageService.getJSON<StoredFilterEntry>('franchise_filter_data')
  if (!parsedData?.data) return

  const context = parseSentryFilterContext(parsedData.data)
  context.timestamp = parsedData.timestamp ? new Date(parsedData.timestamp).toISOString() : 'unknown'

  Sentry.setContext('filter', context as unknown as Record<string, unknown>)

  log('App', 'bootstrap', '✅ Filter context added to Sentry', {
    franchises: context.franchise_count,
    dateRange: context.date_range
  })
}

const handleFilterChange = (filterData?: SentryFilterData): void => {
  if (!filterData) return

  try {
    const context = parseSentryFilterContext(filterData)
    context.last_updated = new Date().toISOString()

    Sentry.setContext('filter', context as unknown as Record<string, unknown>)

    log('App', 'filter-update', '✅ Filter context updated in Sentry', {
      franchises: context.franchise_count,
      dateRange: context.date_range
    })
  } catch (err) {
    log('App', 'filter-update', '⚠️ Failed to update filter context in Sentry', err)
  }
}

export const setupSentry = (
  _app: App,
  permissions: PermissionsData | null,
  eventBus: typeof EventBusInstance
): void => {
  if (permissions?.user) {
    const user = permissions.user as typeof permissions.user & { id?: string; name?: string }
    Sentry.setUser({
      id: user.id,
      email: user.email,
      username: user.name
    })

    const firstFranquia = permissions.franquias?.[0] as { franquia_id?: string } | undefined
    Sentry.setTag('franchise_id', firstFranquia?.franquia_id || 'unknown')
    Sentry.setTag('locale', permissions.locale || 'pt-BR')
  }

  try {
    loadInitialFilterContext()
  } catch (err) {
    log('App', 'bootstrap', '⚠️ Failed to load filter context for Sentry', err)
  }

  eventBus.on<SentryFilterData>('filtro.applied', handleFilterChange)

  log('App', 'bootstrap', '✅ Sentry initialized with comprehensive error tracking')
}
